import type { EditorState } from "prosemirror-state";
import type { Document, Model, Msg } from "../types";

// ProseMirror makes the editors and the node views outside the snabbdom render,
// but they show the Model. render() publishes each Model here before its patch,
// so an editor or a node view that the patch makes reads the new Model.
type Snapshot = Pick<Model, "editorStates" | "cardCache" | "missingCards" | "unreadableCards">;
const EMPTY: Snapshot = { editorStates: {}, cardCache: {}, missingCards: {}, unreadableCards: {} };
let current: Snapshot = EMPTY;
// The snapshot that the watchers saw last.
let notified: Snapshot = EMPTY;
const watchers = new Map<string, Set<() => void>>();

export function publishModel(model: Model): void {
  current = model;
}

export function editorState(id: string): EditorState | undefined {
  return current.editorStates[id];
}

// The content of a card. It is `undefined` until the card loads, and `null`
// when the card does not exist.
export function cardContent(id: string): Document | null | undefined {
  if (current.missingCards[id]) return null;
  return current.cardCache[id]?.content;
}

// The page of a card and a section say this when the editor cannot read the
// card. The reason follows it.
export const UNREADABLE_TEXT =
  "The editor cannot read this card, so editing is off. Thus the editor cannot save over the content.";

// Returns why the editor cannot read a card, or undefined if it can.
export function cardError(id: string): string | undefined {
  return current.unreadableCards[id];
}

// Call `render` now and after each change of the card. If the Model does not
// have the card, load it. Returns the function that stops the watch.
export function watchCard(id: string, dispatch: (msg: Msg) => void, render: () => void): () => void {
  const renders = watchers.get(id) ?? new Set<() => void>();
  watchers.set(id, renders);
  renders.add(render);
  render();
  if (cardContent(id) === undefined) dispatch({ type: "LoadCard", id });
  return () => {
    renders.delete(render);
    if (renders.size === 0 && watchers.get(id) === renders) watchers.delete(id);
  };
}

// Tell the watchers of each card that changed since the last call. A watcher
// can start or stop other watches, so the loop uses a copy.
export function notifyWatchers(): void {
  const before = notified;
  notified = current;
  for (const [id, renders] of [...watchers]) {
    const changed =
      before.cardCache[id]?.content !== current.cardCache[id]?.content ||
      before.missingCards[id] !== current.missingCards[id] ||
      before.unreadableCards[id] !== current.unreadableCards[id];
    if (changed) for (const render of [...renders]) render();
  }
}
